import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  apiError,
  completeAnalysisReview,
  generateReport,
  getAnalysis,
  getEntityAudit,
  listAnalyses,
  listReports,
  regenerateAnalysis,
  retryAnalysis,
} from "../services/AiService";
import { AnalysisListItem, AnalysisView, AuditEntry, ReportListItem } from "../types/ai";
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import { downloadSecureFile } from "../utils/secureFile";
import {
  AiNotice,
  AnalysisStatusChip,
  AuditTable,
  ClassificationChip,
  ErrorAlert,
  Loading,
  PageShell,
  ReasonModal,
  ReviewChip,
  RiskChip,
  UrgentBanner,
  fmtDate,
} from "../component/aces/Shared";

type Tab = "summary" | "findings" | "questions" | "reports" | "audit";
const RUNNING = ["QUEUED", "PROCESSING"];

const AiAnalysisScreen = () => {
  const { id } = useParams();
  const analysisId = Number(id);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [analysis, setAnalysis] = useState<AnalysisView | null>(null);
  const [versions, setVersions] = useState<AnalysisListItem[]>([]);
  const [tab, setTab] = useState<Tab>("summary");
  const [error, setError] = useState<string | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const a = await getAnalysis(analysisId);
      setAnalysis(a);
      setError(null);
      if (RUNNING.includes(a.status)) timer.current = setTimeout(load, 3000);
      listAnalyses(a.checklist.id).then(setVersions).catch(() => setVersions([]));
    } catch (e) {
      setError(apiError(e));
    }
  }, [analysisId]);

  useEffect(() => {
    setAnalysis(null);
    load();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  useEffect(() => {
    if (!analysis) return;
    if (tab === "reports") listReports(analysis.id).then(setReports).catch((e) => setError(apiError(e)));
    if (tab === "audit") getEntityAudit("AI_ANALYSIS", analysis.id).then(setAudit).catch((e) => setError(apiError(e)));
  }, [tab, analysis]);

  const retry = async () => {
    setBusy(true);
    try {
      await retryAnalysis(analysisId);
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (reason: string) => {
    try {
      const next = await regenerateAnalysis(analysisId, reason, (i18n.language || "en").slice(0, 2));
      setRegenOpen(false);
      navigate(`/ai/analysis/${next.id}`);
    } catch (e) {
      setError(apiError(e));
      setRegenOpen(false);
    }
  };

  const completeReview = async (reason: string) => {
    try {
      const updated = await completeAnalysisReview(analysisId, reason || undefined);
      setAnalysis(updated);
      setReviewOpen(false);
      listAnalyses(updated.checklist.id).then(setVersions).catch(() => setVersions([]));
    } catch (e) {
      setError(apiError(e));
      setReviewOpen(false);
    }
  };

  const makeReport = async (type: "DRAFT" | "FINAL") => {
    setBusy(true);
    try {
      await generateReport(analysisId, type, (i18n.language || "en").slice(0, 2));
      setReports(await listReports(analysisId));
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!analysis) {
    return (
      <PageShell title={t("aces.titles.analysis")} crumbs={[{ label: t("aces.nav.reports"), to: "/submittedChecklist" }, { label: t("aces.titles.aiAnalysisCrumb") }]}>
        <ErrorAlert message={error} />
        {!error && <Loading />}
      </PageShell>
    );
  }

  const running = RUNNING.includes(analysis.status);
  const failed = analysis.status === "FAILED";
  const reviewed = analysis.status === "REVIEWED";
  const canAnalyse = hasPermission("AI_ANALYSE");
  const shipment = analysis.checklist.shipment;
  const notRecorded = <span className="aces-missing">{t("aces.analysis.notRecorded")}</span>;
  const awaitingFirstReview = analysis.status === "DRAFT_READY" && analysis.findings.length === 0;

  return (
    <PageShell
      title={
        <>
          {t("aces.titles.analysis")} <span className="aces-muted" style={{ fontSize: 16 }}>{analysis.analysisRef}</span>
        </>
      }
      crumbs={[{ label: t("aces.nav.reports"), to: "/submittedChecklist" }, { label: `${t("aces.titles.aiAnalysisCrumb")} ${analysis.analysisRef}` }]}
      actions={
        canAnalyse && !running ? (
          <button className="aces-btn secondary" onClick={() => setRegenOpen(true)}>
            <i className="ri-refresh-line"></i> {t("aces.analysis.regenerate")}
          </button>
        ) : undefined
      }
    >
      <ErrorAlert message={error} onClose={() => setError(null)} />
      {analysis.hasUrgent && <UrgentBanner />}

      <div className="aces-card">
        <div className="aces-grid-2">
          <dl className="aces-kv">
            <dt>{t("aces.analysis.checklist")}</dt>
            <dd>{analysis.checklist.referenceNo} — {analysis.checklist.title}</dd>
            <dt>{t("aces.ctx.site")}</dt>
            <dd>{analysis.checklist.site || notRecorded}</dd>
            <dt>{t("aces.ctx.process")}</dt>
            <dd>{[analysis.checklist.operationalArea, analysis.checklist.process].filter(Boolean).join(" · ") || notRecorded}</dd>
            <dt>{t("aces.ctx.shipment")}</dt>
            <dd>
              {(shipment && [shipment.awb, shipment.flightNo, [shipment.origin, shipment.destination].filter(Boolean).join(" → ")].filter(Boolean).join(" · ")) || notRecorded}
              {analysis.checklist.awbFromChecklistText && <span className="aces-muted"> ({t("aces.analysis.awbFromText")})</span>}
            </dd>
          </dl>
          <dl className="aces-kv">
            <dt><label htmlFor="analysis-version" className="mb-0">{t("aces.analysis.version")}</label></dt>
            <dd>
              <select id="analysis-version" className="form-control form-control-sm" style={{ maxWidth: 260, display: "inline-block" }} value={analysis.id}
                onChange={(e) => navigate(`/ai/analysis/${e.target.value}`)}>
                {(versions.length ? versions : [{ id: analysis.id, version: analysis.version, status: analysis.status } as AnalysisListItem]).map((v) => (
                  <option key={v.id} value={v.id}>v{v.version} — {t(`aces.enum.analysis.${v.status}`)}</option>
                ))}
              </select>
            </dd>
            <dt>{t("aces.analysis.status")}</dt>
            <dd><AnalysisStatusChip value={analysis.status} /></dd>
            <dt>{t("aces.analysis.methodology")}</dt>
            <dd>
              {analysis.acesEdition || (analysis.legacyAces ? t("aces.analysis.legacy") : t("aces.analysis.noEdition"))}
              {analysis.legacyAces && analysis.acesEdition && <div className="aces-muted">{t("aces.analysis.legacy")}</div>}
            </dd>
            <dt>{t("aces.analysis.reviewedBy")}</dt>
            <dd>{analysis.reviewedBy ? `${analysis.reviewedBy.name} · ${fmtDate(analysis.reviewedAt)}` : "—"}</dd>
          </dl>
        </div>
        <details className="aces-details">
          <summary>{t("aces.analysis.info")}</summary>
          <dl className="aces-kv mt-2 mb-0">
            <dt>{t("aces.analysis.model")}</dt>
            <dd>{analysis.modelId}</dd>
            <dt>{t("aces.analysis.ruleset")}</dt>
            <dd>{analysis.instructionVersion || "—"}</dd>
            <dt>{t("aces.analysis.version")}</dt>
            <dd>v{analysis.version}</dd>
            <dt>{t("aces.analysis.runBy")}</dt>
            <dd>{analysis.triggeredBy?.name || "—"} · {fmtDate(analysis.triggeredAt)}</dd>
            {analysis.regenerateReason && (<><dt>{t("aces.analysis.regenReason")}</dt><dd>{analysis.regenerateReason}</dd></>)}
          </dl>
        </details>
      </div>

      {running && (
        <div className="aces-card" style={{ textAlign: "center" }}>
          <Loading text={t("aces.analysis.running")} />
          <p className="aces-muted">{t("aces.analysis.runningHint")}</p>
        </div>
      )}

      {failed && (
        <div className="aces-error">
          <h5 style={{ color: "#b71c1c" }}>{t("aces.analysis.unavailable")}</h5>
          <p className="mb-2">{t("aces.analysis.unavailableHint")}</p>
          {analysis.errorCode && <p className="aces-muted mb-2">{t("aces.analysis.errorCode")}: {analysis.errorCode}</p>}
          {canAnalyse && (
            <button className="aces-btn" disabled={busy} onClick={retry}>
              <i className="ri-restart-line"></i> {t("aces.analysis.retry")}
            </button>
          )}
        </div>
      )}

      {!running && !failed && (
        <>
          {analysis.status === "SUPERSEDED" && versions[0] && versions[0].id !== analysis.id && (
            <div className="aces-draft-note">
              {t("aces.analysis.supersededNotice", { version: versions[0].version })}{" "}
              <button type="button" className="aces-table-link" onClick={() => navigate(`/ai/analysis/${versions[0].id}`)}>{t("aces.common.view")}</button>
            </div>
          )}
          {!reviewed && <AiNotice />}
          {awaitingFirstReview && (
            <div className="aces-card aces-actions" style={{ justifyContent: "space-between" }}>
              <span>{t("aces.analysis.noFindingsReview")}</span>
              {hasPermission("AI_REVIEW") && (
                <button type="button" className="aces-btn success" onClick={() => setReviewOpen(true)}>
                  <i className="ri-shield-check-line" aria-hidden="true"></i> {t("aces.analysis.completeReview")}
                </button>
              )}
            </div>
          )}
          <div className="aces-tabs" role="tablist">
            {(["summary", "findings", "questions", "reports", "audit"] as Tab[]).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "active" : ""} onClick={() => setTab(k)}>
                {t(`aces.analysis.tabs.${k}`)}
                {k === "findings" ? ` (${analysis.findings.length})` : ""}
              </button>
            ))}
          </div>

          {tab === "summary" && (
            <>
              <div className="aces-card">
                <h5>{t("aces.analysis.executiveSummary")}</h5>
                <p className="aces-preline">{analysis.executiveSummary}</p>
              </div>
              {analysis.overallAssessment && (
                <div className="aces-card">
                  <h5>{t("aces.analysis.overallAssessment")}</h5>
                  <p><b>{t(`aces.enum.rating.${analysis.overallAssessment.rating}`)}</b></p>
                  <p className="aces-preline">{analysis.overallAssessment.narrative}</p>
                </div>
              )}
              <div className="aces-grid-2">
                <div className="aces-card">
                  <h5>{t("aces.analysis.positivePractices")}</h5>
                  {analysis.positivePractices?.length ? <ul>{analysis.positivePractices.map((p, i) => <li key={i}>{p.text}</li>)}</ul> : <p className="aces-muted">—</p>}
                </div>
                <div className="aces-card">
                  <h5>{t("aces.analysis.evidenceGaps")}</h5>
                  {analysis.evidenceGaps?.length ? (
                    <ul>{analysis.evidenceGaps.map((g, i) => <li key={i}>{g.text}{g.impact ? <span className="aces-muted"> — {g.impact}</span> : null}</li>)}</ul>
                  ) : <p className="aces-muted">—</p>}
                </div>
              </div>
              <div className="aces-card">
                <h5>{t("aces.analysis.priorityActions")}</h5>
                {analysis.priorityActions?.length ? (
                  <ul>{analysis.priorityActions.map((p, i) => (
                    <li key={i}><span className="aces-chip grey">{t(`aces.enum.priority.${p.priority}`)}</span> {p.text}</li>
                  ))}</ul>
                ) : <p className="aces-muted">—</p>}
              </div>
              {analysis.limitations && analysis.limitations.length > 0 && (
                <div className="aces-card">
                  <h5>{t("aces.analysis.limitations")}</h5>
                  <ul>{analysis.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
                </div>
              )}
            </>
          )}

          {tab === "findings" && (
            <div className="__defaultTable">
              <table>
                <thead>
                  <tr>
                    <th>{t("aces.finding.ref")}</th>
                    <th>{t("aces.finding.statement")}</th>
                    <th>{t("aces.finding.classification")}</th>
                    <th>{t("aces.finding.risk")}</th>
                    <th>{t("aces.finding.reviewStatus")}</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.findings.length === 0 ? (
                    <tr><td colSpan={5}>{t("aces.analysis.noFindings")}</td></tr>
                  ) : analysis.findings.map((f) => (
                    <tr key={f.id}>
                      <td>
                        <button type="button" className="aces-table-link" onClick={() => navigate(`/ai/finding/${f.id}`)}>{f.findingRef}</button>
                        {f.isUrgent && <div><span className="aces-chip darkred">{t("aces.register.critical")}</span></div>}
                      </td>
                      <td>{f.statement}</td>
                      <td><ClassificationChip value={f.classification} /></td>
                      <td><RiskChip level={f.riskLevel} score={f.riskScore} /></td>
                      <td><ReviewChip value={f.reviewStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "questions" && (
            <div className="__defaultTable aces-stack">
              <table>
                <thead>
                  <tr>
                    <th>{t("aces.pre.question")}</th>
                    <th>{t("aces.pre.answer")}</th>
                    <th>{t("aces.analysis.controlStatus")}</th>
                    <th>{t("aces.finding.evidenceStatus")}</th>
                    <th>{t("aces.analysis.rationale")}</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.questionAssessments.map((q) => (
                    <tr key={q.questionId}>
                      <td>{q.questionText}</td>
                      <td>{q.answer || "—"}{q.reason && <div className="aces-muted">{q.reason}</div>}</td>
                      <td>{t(`aces.enum.control.${q.controlStatus}`)}</td>
                      <td>{t(`aces.enum.evidence.${q.evidenceStatus}`)}</td>
                      <td>
                        {q.rationale}
                        {q.evidenceRefs.length > 0 && <div className="aces-muted">{q.evidenceRefs.join(", ")}</div>}
                        {q.gaps.length > 0 && <div className="aces-confidence-low">{q.gaps.join("; ")}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "reports" && (
            <div className="aces-card">
              <div className="aces-actions mb-3">
                {hasAnyPermission("AI_REVIEW", "AI_ANALYSE", "REPORT_APPROVE") && (
                  <button className="aces-btn secondary" disabled={busy} onClick={() => makeReport("DRAFT")}>
                    <i className="ri-file-text-line"></i> {t("aces.report.generateDraft")}
                  </button>
                )}
                {hasPermission("REPORT_APPROVE") && (
                  <button className="aces-btn success" disabled={busy || !reviewed} onClick={() => makeReport("FINAL")}
                    title={!reviewed ? t("aces.report.finalNeedsReview") : ""}>
                    <i className="ri-shield-check-line"></i> {t("aces.report.generateFinal")}
                  </button>
                )}
                {!reviewed && <span className="aces-muted">{t("aces.report.finalNeedsReview")}</span>}
              </div>
              <div className="__defaultTable">
                <table>
                  <thead>
                    <tr>
                      <th>{t("aces.report.version")}</th>
                      <th>{t("aces.report.status")}</th>
                      <th>{t("aces.report.language")}</th>
                      <th>{t("aces.report.generated")}</th>
                      <th>{t("aces.report.approved")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr><td colSpan={6}>{t("aces.report.none")}</td></tr>
                    ) : reports.map((r) => (
                      <tr key={r.id}>
                        <td>v{r.reportVersion}</td>
                        <td><span className={`aces-chip ${r.status === "FINAL" ? "green" : "amber"}`}>{t(`aces.report.${r.status.toLowerCase()}`)}</span></td>
                        <td>{r.language.toUpperCase()}</td>
                        <td>{r.generatedBy?.name} · {fmtDate(r.generatedAt)}</td>
                        <td>{r.approvedBy ? `${r.approvedBy.name} · ${fmtDate(r.approvedAt)}` : "—"}</td>
                        <td>
                          <button className="aces-btn small secondary"
                            onClick={() => downloadSecureFile(r.fileId, `${analysis.analysisRef}-v${r.reportVersion}-${r.status.toLowerCase()}.pdf`).catch((e) => setError(apiError(e)))}>
                            <i className="ri-download-2-line"></i> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "audit" && <div className="aces-card"><AuditTable entries={audit} /></div>}
        </>
      )}

      <ReasonModal show={reviewOpen} required={false} title={t("aces.analysis.completeReview")} confirmLabel={t("aces.analysis.completeReview")}
        onCancel={() => setReviewOpen(false)} onConfirm={completeReview}>
        <p className="aces-muted">{t("aces.analysis.completeReviewHint")}</p>
      </ReasonModal>
      <ReasonModal show={regenOpen} title={t("aces.analysis.regenerate")} confirmLabel={t("aces.analysis.regenerate")}
        onCancel={() => setRegenOpen(false)} onConfirm={regenerate}>
        <p className="aces-muted">{t("aces.analysis.regenerateHint")}</p>
      </ReasonModal>
    </PageShell>
  );
};

export default AiAnalysisScreen;
